// ****************************************************************************
// Copyright (c) 2019-2024 Markadoc Corp and Ontario Partners LLC of NY.
// All Rights Reserved
// ****************************************************************************
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, Subject, Observable } from "rxjs";
import { Injectable } from "@angular/core";
import { catchError, shareReplay, tap, map, switchMap } from "rxjs/operators";

// Called Services (static)

import { AuthService } from "./auth.service"; // authentication
import { GroupService } from "./group.service";
import { GroupMemberService } from "./group-member.service";
import { OrganizationService } from "./organization.service";
import { OrgUserService } from "./orguser.service";
import { OrgUserInfoService } from "./orguserinfo.service";
import { UserService } from "./user.service";
import { UserSubAccountService } from "./user-subaccount.service";
import { ToasterService } from "../service/toaster.service";
import { ToastrModule, ToastrService } from "ngx-toastr";

// SQL Data Objects
import { CountData } from "../model/countdata.model";
import { SumData } from "../model/sumdata.model";

import { GroupData } from "../group/model/groupdata.model";
import { OrgData } from "../org/model/orgdata.model";
import { OrgUserData } from "../org/model/orguserdata.model";
import { OrgUserInfo } from "../org/model/orguserinfo.model";
import { UserData } from "../user-acct/model/userdata.model";
import { UserInfo } from "../user-acct/model/userinfo.model";

import { environment } from "../../environments/environment";

@Injectable({ providedIn: "any" })
export class AuthorizationService {
  constructor(
    private authsvc: AuthService, // Authentication svc
    private usersvc: UserService,
    private subacctsvc: UserSubAccountService,
    private orgsvc: OrganizationService,
    private orgusersvc: OrgUserService,
    private orguserinfosvc: OrgUserInfoService,
  ) {}

  checkIsUserExpired(uinfo: UserInfo): boolean {
    let expired = false;
    const dtnow = new Date();
    // const sdtnow = dtnow.toISOString();

    if (uinfo) {
      if (uinfo.expires) {
        const usr_exp_dt = new Date(uinfo["expires"]);
        if (usr_exp_dt < dtnow) {
          expired = false;
        } else {
          expired = true;
        }
      } else {
        expired = false;
      }
    } else {
      expired = true;
    }
    return expired;
  }

  async getIsUserExpired(uid: string): Promise<boolean> {
    const uinfo = await this.usersvc.getUserInfoPromise(uid);
    return this.checkIsUserExpired(uinfo);
  }

  async getIsUserExpiredbyEmail(email: string): Promise<boolean> {
    const uinfo = await this.usersvc.getUserByEmailPromise(email);
    return this.checkIsUserExpired(uinfo);
  }

  async getIsUserSubAcct(uid: string): Promise<boolean> {
    // get the owner userinfo by email
    const ludata = await this.subacctsvc.getIsLinkedUser(uid);

    console.log("authz getIsUserSubAcct=", ludata);
    if (!ludata) {
      return false;
    }
    if (ludata && !ludata.owner_uid) {
      return false;
    }
    return true;
  }

  async getUserSubAcctInfo(uid: string): Promise<any> {
    // get the owner userinfo by email
    const luinfo = await this.subacctsvc.getIsLinkedUserInfo(uid);

    if (!luinfo) {
      return null;
    }
    return luinfo;
  }

  async getOwnerSubacctsAvailableByEmail(email: string) {
    // get the owner userinfo by email
    const owner = await this.usersvc.getUserByEmailPromise(email);

    if (!owner) {
      return -1;
    }

    const availcnt = await this.getOwnerSubAcctsAvailable(owner["uid"]);
    const avail = availcnt["count"];

    return avail;
  }

  async getOwnerSubAcctsAvailable(uid: string) {
    let accounts = 0;
    const available = 0;
    // get the owner userinfo
    const owner = await this.usersvc.getUserInfoPromise(uid);

    if (!owner) {
      return -1;
    }

    if (owner && owner["accounts"]) {
      accounts = owner["accounts"];
    }

    const usedcnt = await this.subacctsvc.getOwnerSubAcctsCount(uid);
    const used = usedcnt["count"];
    const avail = accounts - used;

    // return subacct count allowed vs used from linked_users
    return avail;
  }

  getCreateUserGroupAllowed(userData: UserData) {
    return userData;
  }

  async getIsUserGroupAdmin(uid: string): Promise<boolean> {
    let subAcctInfo = null;

    // Check if user is a subaccount guest
    const isSubAcct = await this.getIsUserSubAcct(uid);

    if (!isSubAcct) {
      return true;
    }

    // If user subacct return the group_admin setting
    if (isSubAcct) {
      subAcctInfo = await this.getUserSubAcctInfo(uid);
      console.log("Authzsvc: user subacct info found=", subAcctInfo);
      if (subAcctInfo && subAcctInfo.group_admin) {
        // return the setting
        return subAcctInfo.group_admin;
      }
      // return default
    }
    return false;
  }

  checkIsOrgUserExpired(orginfo: OrgUserInfo): boolean {
    let org_expired = false;
    let expired = false;

    const dtnow = new Date();
    // const sdtnow = dtnow.toISOString();

    // Check org_expires
    //
    if (orginfo) {
      if (orginfo.org_expires) {
        const org_exp_dt = new Date(orginfo["org_expires"]);
        // If org has expiration dt, compare value to now
        if (org_exp_dt >= dtnow) {
          org_expired = false;
        } else {
          org_expired = true;
        }
      } else {
        // No expiration date therefor never expires
        org_expired = false;
      }
    } else {
      org_expired = true;
    }

    // Check org_user_expires
    //
    if (orginfo && !org_expired) {
      if (orginfo.org_user_expires) {
        const usr_exp_dt = new Date(orginfo["org_user_expires"]);
        // If user has expiration dt, compare value to now
        if (usr_exp_dt >= dtnow) {
          expired = false;
        } else {
          expired = true;
        }
      } else {
        // No expiration date therefor never expires
        expired = false;
      }
    } else {
      // no orginfo or org is expired
      expired = true;
    }
    return expired;
  } // check is org expired.

  checkIsOrgUserExpired2(ouinfo: OrgUserInfo): boolean {
    let expired = false;
    const dtnow = new Date();
    // const sdtnow = dtnow.toISOString();

    if (ouinfo) {
      if (ouinfo.org_user_expires) {
        const usr_exp_dt = new Date(ouinfo["expires"]);
        // If user has expiration dt, compare value to now
        if (usr_exp_dt < dtnow) {
          expired = false;
        } else {
          expired = true;
        }
      } else {
        // No expiration date therefor never expires
        expired = false;
      }
    } else {
      expired = true;
    }
    return expired;
  }

  async getIsOrgUserGroupAdmin(oid: string, uid: string): Promise<boolean> {
    // const orgData = await this.orgusersvc.getOrgUserByOrgAndUserPromise( oid, uid );
    // const orgUser = await this.orgusersvc.getOrgUserByOrgAndUserPromise( oid, uid );
    const orgUserInfo =
      await this.orguserinfosvc.getOrgUserInfoByOrgAndUidPromise(oid, uid);
    const orgDataExpired = false;
    const orgUserExpired = false;

    if (!orgUserInfo) {
      console.log("No org user found for orgid/uid=", oid + "/" + uid);
      return false;
    }

    if (orgUserInfo.org_expires) {
      console.log("org_expires=", orgUserInfo.org_expires);
    }

    if (orgUserInfo.org_user_expires) {
      console.log("org_user_expires=", orgUserInfo.org_user_expires);
    }

    if (orgUserInfo) {
      return orgUserInfo["org_user_group_admin"];
    }

    return false;
  }

  async getIsOrgGuestAdmin(oid: string, uid: string): Promise<any> {
    let result = false;
    if (!oid || !uid) {
      return result;
    }
    const ou = await this.orgusersvc.getOrgUserByOrgAndUidPromise(oid, uid);
    console.log("authriz getIsOrgGuestAdmin ou=", ou);
    console.log(
      "authriz getIsOrgGuestAdmin ou.org_user_type==",
      ou.org_user_type,
    );
    console.log(
      "authriz getIsOrgGuestAdmin ou.org_guest_user_admin==",
      ou.org_guest_user_admin,
    );

    if (ou && ou["org_admin"]) {
      result = true;
    }
    if (ou && ou["org_user_type"] !== "G" && ou["org_guest_user_admin"]) {
      result = true;
    }
    return result;
  }

  // *********************************************************
  // Auth Permissions Returned As Observables for Object or
  // Component list buttons or html.
  // *********************************************************

  getIsUserAnOrgAdmin$(oid: string, uid: string): Observable<boolean> {
    return this.orgusersvc.getOrgUserByOrgAndUser$(oid, uid).pipe(
      map((ou) => {
        let result = false;
        if (!ou) {
          return result;
        }
        if (ou && ou["org_admin"] === true) {
          result = true;
          return result;
        } else {
          return result;
        }
      }),
    );
  }

  getIsOrgUserAnOrgAdmin$(ouid: string): Observable<boolean> {
    return this.orgusersvc.getOrgUser$(ouid).pipe(
      map((ou) => {
        let result = false;
        if (!ou) {
          return result;
        }
        if (ou && ou["org_admin"] === true) {
          result = true;
          return result;
        } else {
          return result;
        }
      }),
    );
  }

  /********************************
 // Observable<boolean>
 getIsUserOrgOwner( oid: string, uid: string ): Observable<any>
  {
   return this.usersvc.getUserInfo$( uid )
          .pipe (
            switchMap((user: any) => {
              return this.orgsvc.getOrganization$( oid )
              .pipe(
                 switchMap( (org) => {
                  // Check if org owner email is user email
                  const result = user.email === org.org_owner_email ?
                        true : false;
                  return result;
                 })
              );
            })
          );
   }
************************/

  getIsUserAnOrgGuestAdmin$(oid: string, uid: string): Observable<boolean> {
    return this.orgusersvc.getOrgUserByOrgAndUser$(oid, uid).pipe(
      map((ou) => {
        let result = false;
        if (!ou) {
          return result;
        }
        if (ou && ou["org_user_guest_user_admin"] == true) {
          result = true;
          return result;
        } else {
          return result;
        }
      }),
    );
  }

  getIsOrgUserAnOrgGuestAdmin$(ouid: string): Observable<boolean> {
    return this.orgusersvc.getOrgUser$(ouid).pipe(
      map((ou) => {
        let result = false;
        if (!ou) {
          return result;
        }
        if (ou && ou["org_user_guest_user_admin"] == true) {
          result = true;
          return result;
        } else {
          return result;
        }
      }),
    );
  }

  getDidUserCreateOrgUserFoundByOrgAndUserId$(
    oid: string,
    uid: string,
  ): Observable<boolean> {
    const thisUserData = this.authsvc.getTokenInfo();
    if (thisUserData !== null) {
      return this.orgusersvc.getOrgUserByOrgAndUser$(oid, uid).pipe(
        map((ou) => {
          let result = false;
          if (!ou) {
            return result;
          }
          if (ou && ou["org_user_created_by_user_uid"] === thisUserData.uid) {
            result = true;
            return result;
          } else {
            return result;
          }
        }),
        /**********
             catchError(error => {
               // Handle the error gracefully
                console.error('Error in pipe:', error);
               // Return an observable with a default value or error message
               return null;
             })
             *************/
      );
    }
  }

  getDidUserCreateOrgUserFoundByOrgUserId$(ouid: string): Observable<boolean> {
    const thisUserData = this.authsvc.getTokenInfo();
    if (thisUserData !== null) {
      return this.orgusersvc.getOrgUser$(ouid).pipe(
        map((ou) => {
          let result = false;
          if (!ou) {
            return result;
          }
          if (ou && ou["org_user_created_by_user_uid"] === thisUserData.uid) {
            result = true;
            return result;
          } else {
            return result;
          }
        }),
      );
    }
  }

  /***********
 private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // Client-side error
      console.error('An error occurred:', error.error);
    } else {
      // Backend returned an error
      console.error(`authorization svc  returned code ${error.status}, body was: `, error.error);
    }
    // Return an observable with a user-facing error message
    return throwError(() => new Error('Error in server function call; please try again later.'));
 }
 ************/
} // *** authorization service ***
